import type { StaticEntityType } from "@config";
import { normalizeToNestedData } from "@/shared/utils/normalizer";
import { useQuery } from "@tanstack/react-query";
import { pagesApi } from "@/shared/services/pages/pages.api";
import { apiClient } from "@lib/axios";

const ensureNested = (slides: unknown[]): StaticEntityType[] => {
  return slides.map((slide) => {
    const s = (slide ?? {}) as Record<string, unknown>;
    if (s.id && s.children) return s as unknown as StaticEntityType;
    return normalizeToNestedData([s])[0] as StaticEntityType;
  });
};

export const useStorySlides = (
  storyId: string,
  contentPointer?: string,
  /**
   * A version's slides come from the same pointer when it has one; an older
   * version keeps them on its own record instead, so it is read from there.
   */
  kind: "story" | "version" = "story",
) => {
  return useQuery({
    queryKey: ["pages", "slides", kind, storyId, contentPointer || ""],
    queryFn: async (): Promise<StaticEntityType[]> => {
      if (contentPointer) {
        let response: Response;
        try {
          response = await fetch(contentPointer);
        } catch (error) {
          throw new Error(
            `Failed to fetch story slides from ${contentPointer}: ${(error as Error)?.message ?? "network error"}`,
          );
        }
        if (!response.ok) {
          throw new Error(
            `Failed to fetch story slides from ${contentPointer}: ${response.status} ${response.statusText}`,
          );
        }
        let data: unknown;
        try {
          data = await response.json();
        } catch (error) {
          throw new Error(
            `Failed to parse story slides JSON from ${contentPointer}: ${(error as Error)?.message ?? "parse error"}`,
          );
        }
        const slides =
          data &&
          typeof data === "object" &&
          Array.isArray((data as { slides?: unknown }).slides)
            ? ((data as { slides: unknown[] }).slides as unknown[])
            : [];
        return ensureNested(slides);
      }
      if (kind === "version") {
        const version = (await apiClient.get(`/versions/${storyId}`)) as {
          slides?: unknown[];
          data?: { slides?: unknown[] };
        };
        return ensureNested(version?.slides ?? version?.data?.slides ?? []);
      }
      const slides = await pagesApi.getPageSlides(storyId);
      return ensureNested(slides);
    },
    enabled: !!storyId,
  });
};
